import { Button, Space, Typography } from 'antd';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { t } from '../../../../../helpers/i18n';
import { App } from '../../../../../data/terra/App';
import Terra from '../../../../../services/apis/terra/Terra';
import { useDispatch } from 'react-redux';
import { showError, showLoading } from '../../../../../store/popup/actions';
import { error, Result, success } from '../../../../../util/result';
import { hideLoading } from '../../../../../store/loading/actions';
import { PlatformCode } from '../../../../../data/terra/ModuleConfig/util';
import { downloadPandoraConfig } from '../../../../../util/pandoraConfig';
import { Product } from '../../../../../data/terra/Product';
import ProjectSuccessImage from 'assets/images/v2/img_project_success.png';
import './ProjectSuccessContent.scss';
import { AndroidFilled, AppleFilled } from '@ant-design/icons';

interface CreateProjectProps {
  project: Product;
}

const ProjectSuccessContent: React.FC<CreateProjectProps> = (props) => {
  const { project } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  let apps: App[] = [];

  const openProjectManager = () => {
    history.push(`/v2/projects/${project.id}/app-config`);
  };

  const downloadAndroidConfig = async () => {
    await downloadConfigByPlatformCode(PlatformCode.AndroidNative);
  };

  const downloadIOSConfig = async () => {
    await downloadConfigByPlatformCode(PlatformCode.IOSNative);
  };

  async function downloadConfigByPlatformCode(platformCode: PlatformCode) {
    dispatch(showLoading());
    const appsResult = await downloadAppIfNeeded();

    if (appsResult.isError()) {
      showCommonError();
      return;
    }

    const apps = appsResult.data ?? [];
    const androidApp = apps.find((v: App) => v.platform.code === platformCode);

    if (!androidApp) {
      showCommonError();
      return;
    }

    const appVersionResult = await Terra.app.fetchAppVersions(androidApp.id);
    if (appVersionResult.isError() || !appVersionResult.data?.data[0]) {
      showCommonError();
      return;
    }

    const defaultVersion = appVersionResult.data?.data[0]!;

    const downloadPandoraConfigResult = await downloadPandoraConfig(
      androidApp,
      defaultVersion
    );
    dispatch(hideLoading());
    if (downloadPandoraConfigResult.isError()) {
      dispatch(showError(t('SomethingWentWrong')));
      return;
    }
  }

  function showCommonError() {
    dispatch(hideLoading());
    dispatch(showError(t('SomethingWentWrong')));
  }

  async function downloadAppIfNeeded(): Promise<Result<App[]>> {
    if (apps.length > 0) return success(apps);

    const appsResult = await Terra.app.fetchApps(project.id);
    if (appsResult.isError()) {
      return error(appsResult.exception());
    }

    apps = appsResult.data?.data ?? [];
    return success(apps);
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <img
          className="project-success-logo-style"
          alt="Project Success"
          src={ProjectSuccessImage}
        />

        <p className="project-success-text-create-new-project">
          {t('create_project_success_title')}
        </p>

        <p className="project-success-text-config-guide">
          {t('create_project_success_download_config')}
        </p>

        <div className="project-success-download-section-container">
          <div>
            <p className="project-success-text-project-name">
              {project.name}
            </p>
            <p className="project-success-project-version-style">
              {t('create_project_success_version')}
            </p>
          </div>
          <div className="project-success-divider" />
          <Space className='project-success-clickable-tag' align="center" onClick={downloadIOSConfig} >
            <AppleFilled className='project-success-platform-icon-style'/>
            <div>
              <p className='project-success-project-download-platform-style'>
                {t('create_project_success_download_config_for')}
              </p>
              <p className='project-success-project-download-platform-style'>
                {t('create_project_success_download_config_ios')}
              </p>
            </div>
          </Space>
          <div className="project-success-divider" />
          <Space className='project-success-clickable-tag' align="center" onClick={downloadAndroidConfig}>
            <AndroidFilled className='project-success-platform-icon-style'/>
            <div>
              <p className='project-success-project-download-platform-style'>
                {t('create_project_success_download_config_for')}
              </p>
              <p className='project-success-project-download-platform-style'>
                {t('create_project_success_download_config_android')}
              </p>
            </div>
          </Space>
        </div>
        <Button
          type={'primary'}
          className="project-success-button-open-project-management"
          onClick={openProjectManager}
        >
          {t('create_project_success_open_project_management')}
        </Button>
      </div>

      <div className="project-success-success-project-footer">
        <p className="project-success-text-config-guide">
          {t('create_project_success_integrate_terra')}
        </p>
        <Space align="center" size="large">
          <Typography.Link
            href="https://terra.dev.teko.vn/developer/docs/terraCore/v0/quickstart"
            className="project-success-button-margin"
          >
            {t('create_project_success_guide')}
          </Typography.Link>
        </Space>
      </div>
    </div>
  );
};

export default ProjectSuccessContent;
